import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  BulkActions,
  Button,
  Checkbox,
  Deadline,
  EditableProperty,
  EntityChip,
  LoadMore,
  Money,
  PropertyList,
  PropertyRow,
  Skeleton,
  StatusControl,
  ToastProvider,
  ZonedDateTime,
  useToast,
} from '@/components/ui';
import { Section, Code } from './Section';
import { useTheme } from '@/theme/useTheme';
import { SAMPLE_NOW, STATUS_OPTIONS } from '../workspace/model';

export function WorkspacePatternsSection() {
  const { mode } = useTheme();
  useEffect(() => {
    if (window.location.hash !== '#workspace-patterns') return;
    const section = document.getElementById('workspace-patterns');
    const root = section?.closest('main')?.parentElement;
    if (!section || !root) return;
    const header = root.querySelector('header')?.getBoundingClientRect().height ?? 0;
    root.scrollTo({
      top:
        root.scrollTop + section.getBoundingClientRect().top - root.getBoundingClientRect().top - header - 24,
      behavior: 'instant',
    });
  }, []);
  return (
    <Section
      id="workspace-patterns"
      title="Рабочее пространство"
      lead="Панель внутри окна, свойства рядом с записью, длинные списки и действия с отменой. Все элементы собраны из деталей существующей оболочки."
    >
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <div className="flex flex-wrap items-center gap-4 border-b border-line p-5">
          <div className="min-w-0 flex-1">
            <h3 className="type-heading">Правая боковая панель</h3>
            <p className="mt-1 text-sm text-fg-muted">
              Нажми на запись. Открой второе окно и потяни разделитель: у каждого окна своя панель.
            </p>
          </div>
          <Link
            to="/design/workspace"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-muted px-3 text-xs font-semibold text-fg hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            Открыть образец
            <ArrowRight className="size-4" />
          </Link>
        </div>
        <iframe
          title="Правая боковая панель — рабочий образец"
          loading="lazy"
          key={mode}
          src="/design/workspace?embedded"
          className="h-96 w-full border-0"
        />
        <p className="border-t border-line p-4 text-xs text-fg-muted">
          <Code>WorkspaceCanvas rightPanel</Code> · 384 px; содержимому остаётся 560 px. В узком окне —
          поверх, в самом узком — на всё окно.
        </p>
      </div>
      <ToastProvider>
        <div className="grid items-start gap-6 lg:grid-cols-2">
          <PropertiesDemo />
          <StatusDemo />
          <NumbersDemo />
          <LoadingDemo />
        </div>
      </ToastProvider>
    </Section>
  );
}

function PropertiesDemo() {
  const [note, setNote] = useState('До конца недели');
  return (
    <div className="rounded-lg border border-line bg-card p-5">
      <h3 className="mb-4 type-heading">Список свойств</h3>
      <PropertyList>
        <PropertyRow label="Ответственный">
          <EntityChip
            kind="user"
            name="Анна Смирнова"
            onClick={() => {
              window.location.href = '/design/workspace/person/0';
            }}
          />
        </PropertyRow>
        <PropertyRow label="Сумма">
          <Money minorUnits={4820000} currency="RUB" />
        </PropertyRow>
        <EditableProperty label="Примечание" value={note} onCommit={setNote} />
      </PropertyList>
      <p className="mt-4 text-xs text-fg-muted">
        <Code>PropertyList</Code> · <Code>EditableProperty</Code> · Enter сохраняет, Escape отменяет правку.
      </p>
    </div>
  );
}

function StatusDemo() {
  const [status, setStatus] = useState('review');
  const [selected, setSelected] = useState(false);
  const { toast } = useToast();
  const change = (value: string) => {
    const before = status;
    setStatus(value);
    toast({
      title: 'Статус изменён',
      action: { label: 'Отменить', undo: true, onClick: () => setStatus(before) },
    });
  };
  return (
    <div className="rounded-lg border border-line bg-card p-5">
      <h3 className="mb-4 type-heading">Статус и групповые действия</h3>
      <div className="mb-4 flex items-center gap-4">
        <Checkbox label="Пример записи" checked={selected} onCheckedChange={(v) => setSelected(v === true)} />
        <StatusControl value={status} options={STATUS_OPTIONS} onChange={change} />
      </div>
      <BulkActions count={selected ? 1 : 0} onClear={() => setSelected(false)}>
        <Button variant="secondary" size="sm" onClick={() => change('paid')}>
          Отметить оплаченным
        </Button>
      </BulkActions>
      <p className="mt-4 text-xs text-fg-muted">
        <Code>StatusControl</Code> · <Code>BulkActions</Code> · действие сразу, затем «Отменить» или Ctrl+Z.
      </p>
    </div>
  );
}

function NumbersDemo() {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5 text-sm">
      <h3 className="type-heading">Деньги, сроки и время</h3>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-fg-muted">Сумма</span>
        <Money minorUnits={123456789} currency="RUB" />
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-fg-muted">Корректировка</span>
        <Money minorUnits={-125050} currency="RUB" />
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-fg-muted">Срок</span>
        <Deadline at="2026-10-26T15:00:00Z" now={SAMPLE_NOW} />
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-fg-muted">Просрочено</span>
        <Deadline at="2026-10-22T15:00:00Z" now={SAMPLE_NOW} />
      </div>
      <div className="border-t border-line pt-4">
        <ZonedDateTime at="2026-10-23T13:00:00Z" />
        <p className="mt-2 text-xs text-fg-muted">Переключение МСК / местное — в полном образце.</p>
      </div>
      <p className="text-xs text-fg-muted">
        <Code>Money</Code> · <Code>Deadline</Code> · <Code>ZonedDateTime</Code>
      </p>
    </div>
  );
}

function LoadingDemo() {
  const [count, setCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const load = () => {
    if (loading) return;
    setLoading(true);
    timer.current = window.setTimeout(() => {
      setCount((n) => Math.min(12, n + 3));
      setLoading(false);
    }, 900);
  };
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-card">
      <div className="p-5">
        <h3 className="type-heading">Длинные страницы</h3>
        <p className="mt-1 text-sm text-fg-muted">
          Следующие строки заранее занимают своё место. Шапка таблицы, оглавление, «Наверх» и память прокрутки
          — в полном образце.
        </p>
      </div>
      <div data-scrollport="" className="h-48 overflow-y-auto border-y border-line">
        {Array.from({ length: count }, (_, i) => (
          <div
            key={i}
            className="flex h-10 items-center justify-between gap-4 border-b border-line px-5 text-sm"
          >
            <span>Запись {i + 1}</span>
            <Badge>Загружена</Badge>
          </div>
        ))}
        {loading &&
          Array.from({ length: 3 }, (_, i) => (
            <div
              key={'loading-' + i}
              className="flex h-10 items-center justify-between gap-4 border-b border-line px-5"
            >
              <Skeleton className="w-24" />
              <Skeleton className="w-16" />
            </div>
          ))}
      </div>
      <LoadMore
        state={loading ? 'loading' : count >= 12 ? 'complete' : 'idle'}
        loaded={count}
        total={12}
        onLoad={load}
      />
      <p className="px-5 pb-5 text-xs text-fg-muted">
        <Code>LoadMore</Code> · <Code>ScrollTools</Code> · заглушка той же формы (R31).
      </p>
    </div>
  );
}
